import React, { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, User } from "lucide-react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";

const ROUTE_MAP = {
  "Pilla3": "/pilla3",
  "Reservas": "/vehiculos",
  "Vehículos": "/vehiculos",
  "Compra tu coche con nosotros": "/compra",
  "FAQs": "/faqs",
  "Noticias": "/noticias",
};

const PATH_TO_I18N_KEY = {
  "/pilla3": "navbar.pilla3",
  "/vehiculos": "navbar.vehiculos",
  "/compra": "navbar.compra",
  "/faqs": "navbar.faqs",
  "/noticias": "navbar.noticias",
};

const FALLBACK = Object.entries(ROUTE_MAP).map(([label, to], orden) => ({ id: `f-${orden}`, nombre_seccion: label, activo: true, orden }));

// El selector de idioma ES/EN queda oculto de momento.
// Para reactivarlo, cambia esto a true.
const SHOW_LANGUAGE_SWITCHER = false;

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [sections, setSections] = useState(FALLBACK);
  const { user } = useAuth();

  useEffect(() => {
    axios.get("/api/nav-config").then(r => {
      const data = (r.data || [])
        .filter(s => s.activo)
        .slice()
        .sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0));
      if (data.length > 0) setSections(data);
    }).catch(() => { });
  }, []);

  const links = sections.map(s => {
    const to = ROUTE_MAP[s.nombre_seccion] || "/";
    const i18nKey = PATH_TO_I18N_KEY[to];
    const label = i18nKey ? t(i18nKey) : s.nombre_seccion;
    return { to, label, id: s.id };
  });

  const changeLang = (lng) => { i18n.changeLanguage(lng); };

  return (
    <header className="bg-white sticky top-0 z-40 border-b border-neutral-200" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-2 opacity-80 hover:opacity-100 transition-all duration-300 hover:drop-shadow-[0_0_14px_rgba(225,6,0,0.55)]" data-testid="navbar-logo">
          <Logo />
        </Link>
        <nav className="hidden lg:flex items-center gap-7">
          {links.map((l) => (
            <NavLink
              key={l.id}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-sm uppercase tracking-wider font-semibold link-hover-red ${isActive ? "text-[var(--pc-red)]" : "text-black"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link to="/mi-perfil" className="text-black link-hover-red flex items-center gap-1 text-sm uppercase tracking-wider font-semibold" data-testid="nav-mi-perfil">
            <User size={16} /> {user ? t("navbar.perfil") : t("navbar.entrar")}
          </Link>
          {SHOW_LANGUAGE_SWITCHER && <LanguageSwitcher current={i18n.language} onChange={changeLang} />}
        </nav>
        <div className="lg:hidden flex items-center gap-3">
          {SHOW_LANGUAGE_SWITCHER && <LanguageSwitcher current={i18n.language} onChange={changeLang} compact />}
          <button
            className="text-black"
            onClick={() => setOpen(o => !o)}
            data-testid="navbar-mobile-toggle"
            aria-label="menu"
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden border-t border-neutral-200 bg-white">
          <div className="flex flex-col p-4 gap-3">
            {links.map((l) => (
              <Link
                key={`m-${l.id}`}
                to={l.to}
                onClick={() => setOpen(false)}
                className="text-black uppercase text-sm font-semibold link-hover-red"
                data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {l.label}
              </Link>
            ))}
            <Link to="/mi-perfil" onClick={() => setOpen(false)} className="text-black uppercase text-sm font-semibold link-hover-red">{t("navbar.miPerfil")}</Link>
          </div>
        </div>
      )}
    </header>
  );
}

function LanguageSwitcher({ current, onChange, compact = false }) {
  const lang = (current || "es").slice(0, 2);
  const other = lang === "es" ? "en" : "es";
  return (
    <button
      onClick={() => onChange(other)}
      className="text-black text-xs uppercase font-semibold tracking-wider border border-neutral-300 rounded px-2 py-1 hover:border-[var(--pc-red)] hover:text-[var(--pc-red)] transition-colors"
      data-testid="navbar-lang-switch"
      aria-label="Cambiar idioma"
    >
      {lang.toUpperCase()}
    </button>
  );
}

function Logo() {
  return (
    <div className="flex items-center" data-testid="navbar-logo-img-wrap">
      <img
        src="https://res.cloudinary.com/ot3yqjzj/image/upload/v1783599313/logo_cdkk3s.webp"
        alt="pillauncoche"
        className="h-10 w-auto object-contain"
        data-testid="navbar-logo-img"
      />
    </div>
  );
}
